import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { history } from '../../../utils';
import { ADMIN_FLAG_TO_ROLE_NAME } from '../../../constants';
import { MovaticFieldGroupCheck } from '../../../components/Redesing/movatic-field-group';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageContainer from '../../../components/Redesing/page-container';
import PageContent from '../../../components/Redesing/page-content';
import CardView from '../../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import CustomToast from '../../../components/Redesing/custom-toast';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import {
  assignOrgRoleMutation,
  removeAdminOrganizationRoleMutation,
  removeAdminFromOrganizationMutation,
} from '../../../hooks/admins/mutation/V2';
import {
  fetchOrgAdminUserByEmail,
  OrgAdminUserLookup,
} from '../../../hooks/admins/query/V2/useOrgAdminUserRelay';
import useOrganization from '../../../hooks/use-organization';
import useQueryString from '../../../hooks/queryParams/useQueryString';
import {
  PERMISSION_FIELDS,
  PermissionFlags,
  emptyPermissionFlags,
} from '../../../util/adminPermissions';

// The baseline "Read Only" role is granted separately at creation and is
// intentionally not shown/editable here. Derive the checked permission flags
// from the admin's active role names.
const flagsFromLookup = (lookup: OrgAdminUserLookup | null): PermissionFlags => {
  const flags = emptyPermissionFlags();
  if (!lookup) return flags;
  PERMISSION_FIELDS.forEach((field) => {
    const roleName = (ADMIN_FLAG_TO_ROLE_NAME as Record<string, string>)[field.name];
    flags[field.name] = Boolean(roleName && lookup.roleRowsByName[roleName]);
  });
  return flags;
};

const OrganizationAdminDetails: React.FC = () => {
  const { email } = useParams<{ email: string }>();
  const organizationInfo = useOrganization();
  const organizationId = organizationInfo?.id;

  const [lookup, setLookup] = useState<OrgAdminUserLookup | null>(null);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [confirm, setConfirm] = useState(false);
  // In-progress edits; only meaningful while `showEdit` is true.
  const [editFlags, setEditFlags] = useState<PermissionFlags>(emptyPermissionFlags());
  const lookupRef = useRef<OrgAdminUserLookup | null>(null);

  const refreshLookup = useCallback(
    async (showLoading: boolean = true) => {
      if (!email || !organizationId) return;
      if (showLoading) setRolesLoading(true);
      try {
        const result = await fetchOrgAdminUserByEmail(email, organizationId);
        lookupRef.current = result;
        setLookup(result);
      } catch {
        lookupRef.current = null;
        setLookup(null);
      } finally {
        if (showLoading) setRolesLoading(false);
      }
    },
    [email, organizationId]
  );

  useEffect(() => {
    refreshLookup();
  }, [refreshLookup]);

  const currentFlags = flagsFromLookup(lookup);
  const displayFlags = showEdit ? editFlags : currentFlags;

  const startEdit = () => {
    setEditFlags(flagsFromLookup(lookupRef.current));
    setShowEdit(true);
  };

  // Arriving with ?edit=1 (the Edit action on the admins list) opens edit mode
  // once the lookup is loaded, honoring the same gate as the Edit button.
  const wantsEdit = useQueryString().queryParams.get('edit') === '1';
  const autoEditDoneRef = useRef(false);
  useEffect(() => {
    if (wantsEdit && !autoEditDoneRef.current && !rolesLoading && lookup) {
      autoEditDoneRef.current = true;
      startEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsEdit, rolesLoading, lookup]);

  const cancelEdit = () => {
    setShowEdit(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setEditFlags((prev) => ({ ...prev, [name]: checked }));
  };

  // Diff the edited flags against the admin's active roles and apply the delta:
  // assign newly-checked roles, revoke newly-unchecked ones. Roles are the source
  // of truth (there is no legacy REST `access` for org admins).
  const save = async () => {
    const current = lookupRef.current;
    if (!current || !organizationId) {
      CustomToast({ type: 'error', message: 'Unable to update this admin.' });
      return;
    }

    const active = flagsFromLookup(current);
    const calls: Promise<{ isSuccess: boolean }>[] = [];

    PERMISSION_FIELDS.forEach((field) => {
      const roleName = (ADMIN_FLAG_TO_ROLE_NAME as Record<string, string>)[field.name];
      if (!roleName) return;
      const has = editFlags[field.name];
      const had = active[field.name];
      if (has && !had) {
        calls.push(
          assignOrgRoleMutation({
            adminId: current.adminId,
            organizationId,
            roleName,
          })
        );
      } else if (!has && had) {
        const rowId = current.roleRowsByName[roleName];
        if (rowId) {
          calls.push(removeAdminOrganizationRoleMutation(rowId));
        }
      }
    });

    setShowEdit(false);

    if (calls.length === 0) return;

    setRolesLoading(true);
    await CustomAsyncToast({
      promise: () =>
        Promise.allSettled(calls).then((results) => {
          const anyFailed = results.some(
            (r) => r.status === 'rejected' || !r.value.isSuccess
          );
          if (anyFailed) {
            throw new Error('Some roles failed to update');
          }
          return results;
        }),
      successMessage: () => 'Permissions updated',
      loadingMessage: 'Updating permissions...',
      errorMessage: 'Some permissions could not be updated',
    });
    // Re-fetch either way so the checkboxes reflect what actually persisted.
    await refreshLookup(false);
    setRolesLoading(false);
  };

  const removeAdmin = () => {
    if (!email || !organizationId) {
      CustomToast({ type: 'error', message: 'Failed to remove admin' });
      return;
    }
    CustomAsyncToast({
      promise: () =>
        removeAdminFromOrganizationMutation({
          email,
          organizationId,
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors?.[0]?.message || 'Failed to remove admin');
          }
          return res;
        }),
      successMessage: () => 'Admin removed successfully',
      loadingMessage: 'Removing admin...',
      errorMessage: 'Error removing admin',
    }).then((response) => {
      if (response) {
        history.push({
          pathname: '/settings/admin',
          search: `?organizationId=${organizationId}`,
        });
      }
    });
  };

  const button = () => {
    if (showEdit) {
      return (
        <IconButtonMenu
          buttons={[
            { label: 'Cancel', onClick: cancelEdit },
            { label: 'Save', onClick: save, startIcon: <SaveIcon /> },
          ]}
        />
      );
    }
    return (
      <IconButtonMenu
        buttons={[
          {
            label: 'Edit',
            onClick: startEdit,
            startIcon: <EditIcon />,
            disabled: !lookup,
          },
          {
            label: 'Remove',
            onClick: () => setConfirm(true),
            startIcon: <DeleteIcon />,
          },
        ]}
      />
    );
  };

  const content = () => (
    <Stack spacing={2}>
      <CardView
        title={'Admin Permissions'}
        isLoading={rolesLoading}
        content={
          <Stack px={2} pb={2}>
            {PERMISSION_FIELDS.map((field) => (
              <MovaticFieldGroupCheck
                key={field.name}
                id={field.name}
                name={field.name}
                label={field.label}
                description={field.description}
                onChange={handleChange}
                checked={displayFlags[field.name]}
                disabled={!showEdit}
              />
            ))}
          </Stack>
        }
      />
    </Stack>
  );

  const modal = () => (
    <MovaticCustomModal
      title={'Remove Admin'}
      open={confirm}
      onClose={() => setConfirm(false)}
      content={
        <div>
          Are you sure you would like to remove this admin from the organization{' '}
          <b>{organizationInfo?.name}</b>?
        </div>
      }
      action={
        <IconButtonMenu
          buttons={[
            { label: 'Cancel', onClick: () => setConfirm(false) },
            {
              label: 'Remove',
              onClick: () => removeAdmin(),
              startIcon: <DeleteIcon />,
            },
          ]}
        />
      }
    />
  );

  const displayName = lookup ? `${lookup.firstName} ${lookup.lastName}`.trim() : '';

  return (
    <PageContainer isLoading={!organizationId}>
      <PageContent
        backLink={`/settings/admin?organizationId=${organizationId}`}
        contentTitle={'Organization Admins'}
        itemName={displayName || email}
        itemChipValue={email}
        chipLabel={'Admin Email'}
        currentTabV={'general'}
        itemTopButtons={button()}
        tabsList={[{ label: 'General', value: 'general' }]}
        tabsChildren={<>{content()}</>}
      />
      {modal()}
    </PageContainer>
  );
};

export default OrganizationAdminDetails;
